import React from "react";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useAppToast } from "@/hooks/useAppToast";
import type { PaymentMethodAdapter, PaymentAdapterContext } from "../types";
import { PAYMENT_AWAITING_HINT } from "../../components/checkoutPaymentLabels";

const MercadoPagoPixView: React.FC<PaymentAdapterContext> = ({
  session,
  amount,
  hideTitle = false,
}) => {
  const toast = useAppToast();
  const pixCopyPaste =
    session.metadata?.pixCopyPaste ||
    session.metadata?.pixQrCode ||
    "Código PIX indisponível";

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(pixCopyPaste);
      toast.success("Código PIX copiado");
    } catch {
      toast.error("Não foi possível copiar. Selecione o código e copie manualmente.");
    }
  };

  const canShowQr = pixCopyPaste !== "Código PIX indisponível" && pixCopyPaste.length > 10;
  
  // Memoize QR code to prevent re-render on unrelated state changes
  const qrCode = React.useMemo(() => {
    if (!canShowQr) return null;
    return <QRCodeSVG value={pixCopyPaste} size={180} level="M" includeMargin />;
  }, [pixCopyPaste, canShowQr]);

  return (
    <Card id="mercadopago-pix-adapter">
      {hideTitle ? null : (
        <CardHeader>
          <h2 className="text-xl font-semibold">Pague com PIX</h2>
        </CardHeader>
      )}
      <CardContent className={hideTitle ? "pt-6" : undefined}>
        <div className="space-y-4">
          <p className="text-text-secondary text-sm">
            Escaneie o QR Code no app do banco ou copie o código abaixo. Valor:{" "}
            <strong>R$ {amount.toFixed(2).replace(".", ",")}</strong>.
          </p>

          {canShowQr ? (
            <div className="flex flex-col items-center gap-4 rounded-lg border border bg-background p-6">
              {qrCode}
              <p className="text-xs text-text-tertiary text-center">
                No app do banco: PIX → Pagar com QR Code
              </p>
            </div>
          ) : null}

          <div className="rounded-lg border border-dashed border-light bg-surface p-4">
            <p className="text-xs text-text-tertiary mb-2">Código PIX (copia e cola)</p>
            <p className="font-mono text-sm break-all text-foreground">{pixCopyPaste}</p>
          </div>
          <Button type="button" variant="outline" onClick={handleCopy} className="w-full">
            Copiar código PIX
          </Button>
          <p className="text-xs text-text-secondary">{PAYMENT_AWAITING_HINT}</p>
        </div>
      </CardContent>
    </Card>
  );
};

export const MercadoPagoPixAdapter: PaymentMethodAdapter = {
  provider: "MercadoPago",
  method: "Pix",
  render: (context) => <MercadoPagoPixView {...context} />,
};
